// API helpers for the cloud Rules page (QROS scope-grammar Form 3).
//
// A rule is a named conjunctive pattern referenced as `rule:<name>` scope: a
// `then`-less rule is a pure selector whose `when` extension is the set it
// denotes; a rule with a `then` clause also derives statements under forward
// chaining. QROS-only — the endpoints reject the RDF backend and the page is
// gated on isQros. Mirrors useOntology.ts: a react-query read hook plus plain
// async mutation functions the page invokes and then invalidates.

import { useQuery } from '@tanstack/react-query';
import { apiFetch, type ApiResult } from './api';
import { useApiSession } from './useApiSession';

/** Mirror of `RuleDef` from tres-shared-substrate/src/types.rs. `when`/`then` are
 *  RAW QQL text (the native rule model): the WHEN conjunction and the optional THEN
 *  derivation, verbatim — the same syntax authored in a `.tqs` `@rules` block. The
 *  engine parses them; the UI just carries the text. */
export interface RuleDef {
  name: string;
  description: string;
  when: string;
  then: string;
  /** The named ruleset the rule was loaded from, when it was loaded as part of
   *  one (`@rules "name" { … }`). Read-only here: membership comes from the
   *  file, and an edit keeps it. Absent for rules authored in the app. */
  ruleset?: string;
  /** Whether the rule is in effect. A rule switched off stays stored and
   *  listed, derives nothing, and resolves no scope. Rules in the reserved
   *  `system` set can be switched but not edited or deleted. */
  enabled: boolean;
  /** Why the rule does not run, when it does not: its condition or
   *  consequence failed to compile, so the derivation pass skipped it. */
  problem?: string;
}

/** The ruleset every rule TReS ships belongs to. */
export const SYSTEM_RULESET = 'system';

export function isSystemRule(rule: { ruleset?: string }): boolean {
  return rule.ruleset === SYSTEM_RULESET;
}

/** Mirror of `RuleDerivations` — facts in the inferred layer by producer (what
 *  each rule's THEN currently holds), and the rules whose WHEN names a term
 *  reached only through vocabulary `:broader` propagation — what stops
 *  matching if BroaderPropagation is switched off. */
export interface RuleDerivations {
  rules: { name: string; derived: number }[];
  ladder_dependents: string[];
  /** Why the last derivation run failed, while it stands; the change that
   *  triggered it was saved. */
  failure?: string | null;
}

export interface RuleMember {
  uri: string;
  pref_label: string;
}

/** Mirror of `UnknownTerm` — a term the WHEN names that the store has never
 *  seen. `message` is the ready-to-show sentence (reason plus suggestion). */
export interface UnknownTerm {
  term: string;
  suggestion: string | null;
  message: string;
}

/** Mirror of `RuleExtension` — the computed `when` extension under the closed
 *  world, resolved to labels and capped. `unknown_terms` is populated only for
 *  an empty extension: non-empty means the WHEN misnames something, so the
 *  emptiness is an authoring error rather than a fact about the data. */
export interface RuleExtension {
  members: RuleMember[];
  total: number;
  truncated: boolean;
  unknown_terms: UnknownTerm[];
}

export function useRules(enabled: boolean) {
  const { token, ready } = useApiSession();
  return useQuery<RuleDef[]>({
    queryKey: ['rules', token ? 'auth' : 'noauth'],
    enabled: enabled && ready,
    queryFn: async () => {
      const r = await apiFetch<RuleDef[]>('/api/v1/rules', token);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

export function useRuleDerivations(enabled: boolean) {
  const { token, ready } = useApiSession();
  return useQuery<RuleDerivations>({
    queryKey: ['rules-derivations', token ? 'auth' : 'noauth'],
    enabled: enabled && ready,
    queryFn: async () => {
      const r = await apiFetch<RuleDerivations>('/api/v1/rules/derivations', token);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

export async function createRule(
  body: RuleDef,
  accessToken: string,
): Promise<ApiResult<{ name: string }>> {
  return apiFetch<{ name: string }>('/api/v1/rules', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateRule(
  body: RuleDef,
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch<null>('/api/v1/rules', accessToken, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteRule(
  name: string,
  accessToken: string,
): Promise<ApiResult<null>> {
  return apiFetch<null>(
    `/api/v1/rules?name=${encodeURIComponent(name)}`,
    accessToken,
    { method: 'DELETE' },
  );
}

export async function testRule(
  body: RuleDef,
  accessToken: string,
): Promise<ApiResult<RuleExtension>> {
  return apiFetch<RuleExtension>('/api/v1/rules/test', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Mirror of the name-audit report — stored names that predate the naming
 *  policy (case collisions, illegal characters). Legal history that keeps
 *  working; cleanup is a deliberate act, so the audit only reports. */
export interface RuleNameAudit {
  total_rules: number;
  case_collisions: { name: string; collides_with: string }[];
  invalid_names: { name: string; reason: string }[];
}

export function useRuleNameAudit(enabled: boolean) {
  const { token, ready } = useApiSession();
  return useQuery<RuleNameAudit>({
    queryKey: ['rules-name-audit', token ? 'auth' : 'noauth'],
    enabled: enabled && ready,
    queryFn: async () => {
      const r = await apiFetch<RuleNameAudit>('/api/v1/rules/name-audit', token);
      if (!r.ok) {
        throw new Error(r.message);
      }
      return r.data;
    },
  });
}

/** The `rule:<name>` specifier a user pastes into a `@context` tag to scope a
 *  statement to this rule's extension. */
export function ruleScopeToken(name: string): string {
  return `rule:${name}`;
}

/** A rule with no THEN clause is a pure selector (the common scope-grammar
 *  shape); one with a THEN clause also derives statements. */
export function isSelector(rule: { then: string }): boolean {
  return rule.then.trim().length === 0;
}
